import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { driversSelector } from 'slices/drivers/driversSlice';
import { Form, Formik, FormikProps } from 'formik';
import XGSFormInput from 'ui-components/form/input/xgsFormInput';
import { LabelModes } from 'ui-components/molecules/labeled-inputs/labeledInput';
import XGSFormSelect from 'ui-components/form/select/xgsFormSelect';
import { cdlClassOptions, DriverDetails, DriverRequest, driverStatuses, driverValidation } from 'app/data/drivers/models';
import XGSFormPhoneInput from 'ui-components/form/phoneInput/xgsFormPhoneInput';
import XGSFormDate from 'ui-components/form/date/xgsFormDate';
import Address from './address';
import { XGSSelectOption } from 'ui-components/xgs-select/xgsSelect';
import TerminalSummaryState from 'slices/terminal-summary/TerminalSummaryState';
import { getTerminalsList, terminalSummarySelector } from 'slices/terminal-summary/terminalSummarySlice';
import { Popover } from 'ui-components/popover/popover';
import { useDriverDetails } from '../hooks/useDriverDetails';
import Button, { ButtonSizes, ButtonThemes } from 'ui-components/button/button';
import { useMutation } from "@tanstack/react-query";
import { toast } from 'react-toastify';
import { ERROR_MESSAGES } from 'app/data/common/errorMessages';
import DriverService from 'app/data/drivers/driversService';

interface TerminalOption {
    value: string;
    label: string;
}

const EditDriverDetails = ({ isEditing, onCancel }: { isEditing: boolean, onCancel: () => void }) => {
    const dispatch = useDispatch();

    const driverState = useSelector(driversSelector);
    const formRef = React.useRef<FormikProps<DriverDetails>>(null);

    const { driverDetails } = useDriverDetails(driverState?.selectedDriver?.id?.toString() || "");

    const terminalSummaryState: TerminalSummaryState = useSelector(terminalSummarySelector);
    const [terminalOptions, setTerminalOptions] = useState<TerminalOption[]>([]);
    const [terminal, setTerminal] = useState<XGSSelectOption | null>(null);
    const [phoneFieldValue, setPhoneFieldValue] = useState<string>("");

    const updateDriverMutation = useMutation({
        mutationFn: (data: DriverRequest) => {
            if (driverState?.selectedDriver?.id) {
                return DriverService.getInstance().updateDriver(
                    driverState.selectedDriver.id.toString(),
                    data
                );
            }
            return Promise.reject();

        },
        onSuccess: () => {
            toast.info("Driver updated successfully");
        },
        onError: (e: any) => {
            if (e instanceof Error) {
                toast.error(e.message);
            }
            else if (e?.response?.data?.message) {
                toast.error(e.response.data.message);
            }
            else if (typeof e?.message === "string") {
                toast.error(e.message);
            }
            else {
                toast.error(ERROR_MESSAGES.COMMON);
            }
        }
    });

    useEffect(() => {
        if (terminalSummaryState.terminallist) {
            const terminalOptions = terminalSummaryState.terminallist?.map((terminal) => ({
                value: terminal.terminalNumber.toString(),
                label: terminal.terminalName.toLowerCase().capitalize()
            })) || [];
            setTerminalOptions(terminalOptions);
        }
    }, [terminalSummaryState.terminallist]);

    useEffect(() => {
        dispatch(getTerminalsList(0, null, null, "ACTIVE", "XGS Terminal", true));
        // eslint-disable-next-line
    }, []);

    return (
        <>
            <Formik
                initialValues={
                    {
                        driverId: isEditing ? driverDetails?.data?.driverId ?? "" : "",
                        firstName: isEditing ? driverDetails?.data?.firstName ?? "" : "",
                        lastName: isEditing ? driverDetails?.data?.lastName ?? "" : "",
                        commercialDriverLicense: isEditing ? driverDetails?.data?.commercialDriverLicense?.licenseNumber ?? "" : "",
                        licenseClass: isEditing ? driverDetails?.data?.commercialDriverLicense?.licenseClass ?? "" : "",
                        street: isEditing ? driverDetails?.data?.address?.street ?? "" : "",
                        city: isEditing ? driverDetails?.data?.address?.city ?? "" : "",
                        state: isEditing ? driverDetails?.data?.address?.state ?? "" : "",
                        zipcode: isEditing ? driverDetails?.data?.address?.zipcode ?? "" : "",
                        phone: isEditing ? driverDetails?.data?.phone ?? "" : "",
                        domicileTerminalNumber: isEditing ? driverDetails?.data?.domicileTerminalNumber ?? "" : "",
                        status: isEditing ? driverDetails?.data?.status ?? "" : "",
                        expirationDate: isEditing ? driverDetails?.data?.commercialDriverLicense?.expirationDate ?? "" : "",
                    }
                }
                enableReinitialize
                validationSchema={driverValidation()}
                innerRef={formRef}
                onSubmit={(values) => {
                    const payload = {
                        driverId: values.driverId,
                        firstName: values.firstName,
                        lastName: values.lastName,
                        phone: phoneFieldValue.length > 0 ? "+" + phoneFieldValue : values.phone,
                        domicileTerminalNumber: !!terminal ? terminal.value : values.domicileTerminalNumber,
                        status: values.status,
                        address: {
                            street: values.street,
                            city: values.city,
                            state: values.state,
                            zipcode: values.zipcode,
                        },
                        commercialDriverLicense: {
                            licenseNumber: values.commercialDriverLicense,
                            licenseClass: values.licenseClass,
                            expirationDate: values.expirationDate,
                        },
                    }
                    updateDriverMutation.mutate(payload as DriverRequest);
                }}
            >
                {({ values, setFieldValue }: FormikProps<DriverDetails>) => (
                    <Form>
                        {isEditing && <div className='drivers-sidebar__header'>
                            <div className='drivers-sidebar__title'>Driver Information</div>
                            <div className='drivers-sidebar__action'>
                                <Button
                                    theme={ButtonThemes.gray}
                                    size={ButtonSizes.small}
                                    onClick={onCancel}
                                >Cancel
                                </Button>
                                <Button
                                    theme={ButtonThemes.blue}
                                    size={ButtonSizes.small}
                                    type="button"
                                    onClick={() => formRef.current?.submitForm()}

                                >Save changes
                                </Button>
                            </div>
                        </div>
                        }
                        <div className='drivers-sidebar__content'>
                            <div className='drivers-sidebar__content__item'>
                                <div className='drivers-sidebar__content__item__label'>Driver ID{' '}
                                    <span className='drivers-sidebar__content__item__auto-generated'>
                                        (Auto-generated)</span></div>
                                <div className='drivers-sidebar__content__item__value'>
                                    <XGSFormInput
                                        type="text"
                                        name="driverId"
                                        disabled
                                        labelMode={LabelModes.column}
                                    />
                                </div>
                            </div>
                            <div className='drivers-sidebar__content__item'>
                                <div className='drivers-sidebar__content__item__value'>
                                    <XGSFormInput
                                        type="text"
                                        name="firstName"
                                        required={true}
                                        labelMode={LabelModes.column}
                                        label="First Name"
                                    />
                                </div>
                            </div>
                            <div className='drivers-sidebar__content__item'>
                                <div className='drivers-sidebar__content__item__value'>
                                    <XGSFormInput
                                        type="text"
                                        name="lastName"
                                        required={true}
                                        labelMode={LabelModes.column}
                                        label="Last Name"
                                    />
                                </div>
                            </div>
                            <div className='drivers-sidebar__content__item'>
                                <div className='drivers-sidebar__content__item__value'>
                                    <XGSFormInput
                                        type="text"
                                        name="commercialDriverLicense"
                                        required={true}
                                        labelMode={LabelModes.column}
                                        label="CDL #"
                                    />
                                </div>
                            </div>
                            <div className='drivers-sidebar__content__item'>
                                <div className='drivers-sidebar__content__item__value'>
                                    <XGSFormSelect
                                        label="CDL Class"
                                        name="licenseClass"
                                        required={true}
                                        options={cdlClassOptions}
                                        value={isEditing ? cdlClassOptions.find(option => option.value ===
                                            driverDetails?.data?.commercialDriverLicense?.licenseClass) : null}
                                        formik
                                        labelMode={LabelModes.column}
                                    />
                                </div>
                            </div>
                            <div className='drivers-sidebar__content__item'>
                                <div className='drivers-sidebar__content__item__value'>
                                    <XGSFormPhoneInput
                                        name="phone"
                                        label="Phone:"
                                        labelMode={LabelModes.column}
                                        onValueChange={(value) => {
                                            setFieldValue("phone", value);
                                            setPhoneFieldValue(value.replace(/\D/g, ''));
                                        }}
                                        value={values.phone}
                                        required={true}
                                        requiredAsteriskDisabled={false}
                                    />
                                </div>
                            </div>
                            <div className='drivers-sidebar__content__item'>
                                <div className='drivers-sidebar__content__item__subtitle'>Address</div>
                                <div className='drivers-sidebar__content__item__value'>
                                    <Address />
                                </div>
                            </div>
                            <div className='drivers-sidebar__content__item'>
                                <div className='drivers-sidebar__content__item__subtitle'>Additional Information</div>
                                <div className='drivers-sidebar__content__item__label'>Terminal{' '}
                                    <span className='drivers-sidebar__content__item__label__required'>*</span>{' '}
                                    <Popover values={<div className='driver-details-tooltip'>
                                        The driver’s assigned work location or dispatch point.
                                    </div>} />
                                </div>
                                <div className='drivers-sidebar__content__item__value'>
                                    <XGSFormSelect
                                        name="terminal"
                                        required={true}
                                        options={terminalOptions}
                                        value={isEditing ? terminalOptions.find(option => option.value ===
                                            driverDetails?.data?.domicileTerminalNumber) : null}
                                        onValueChange={(value) => {
                                            setTerminal(value || null);
                                        }}
                                        formik
                                        labelMode={LabelModes.column}
                                    />
                                </div>
                            </div>
                            <div className='drivers-sidebar__content__item'>
                                <div className='drivers-sidebar__content__item__value'>
                                    <XGSFormSelect
                                        label="Status"
                                        name="status"
                                        required={true}
                                        options={driverStatuses}
                                        value={isEditing ? driverStatuses.find(option => option.value ===
                                            driverDetails?.data?.status) : null}
                                        formik
                                        labelMode={LabelModes.column}
                                    />
                                </div>
                            </div>
                            <div className='drivers-sidebar__content__item'>
                                <div className='drivers-sidebar__content__item__label'>License Expiration{' '}
                                    <span className='drivers-sidebar__content__item__label__required'>*</span></div>
                                <div className='drivers-sidebar__content__item__value'>
                                    <XGSFormDate
                                        name="expirationDate"
                                        onChange={() => { }}
                                        onDateChange={(v) => setFieldValue("expirationDate", v)}
                                    />
                                </div>
                            </div>
                        </div>
                    </Form>)}
            </Formik >
        </>
    );
};

export default EditDriverDetails;
